export const dataStructuresAlgorithmsCourse = {
  id: 'data-structures-algorithms',
  title: 'Data Structures and Algorithms',
  description: 'Comprehensive study of fundamental data structures, algorithm design techniques, complexity analysis, and practical implementation with focus on problem-solving and coding interview preparation.',
  status: 'published',
  credits: 4,
  duration: '15 weeks',
  level: 'Intermediate',
  category: 'Coding',
  learningObjectives: [
    'Master fundamental data structures: arrays, linked lists, stacks, queues, trees, graphs',
    'Understand and apply algorithm design techniques: divide-and-conquer, dynamic programming, greedy algorithms',
    'Analyze time and space complexity using Big O notation',
    'Implement efficient sorting and searching algorithms',
    'Solve complex programming problems using appropriate data structures and algorithms',
    'Prepare for technical coding interviews and competitive programming'
  ],
  prerequisites: ['Object-Oriented Programming', 'Introduction to Computer Science', 'Mathematical foundations'],
  skills: [
    'Data Structure Implementation',
    'Algorithm Design and Analysis',
    'Complexity Analysis',
    'Problem Solving',
    'Code Optimization',
    'Technical Interview Preparation',
    'Recursive Thinking',
    'Graph Algorithms',
    'Dynamic Programming',
    'Sorting and Searching',
    'Hash Tables and Maps',
    'Tree Traversal Algorithms'
  ],
  marketDemand: 'Very High',
  averageSalary: '$85,000 - $180,000',
  careerPaths: [
    'Software Engineer',
    'Senior Software Developer',
    'Algorithm Engineer',
    'Data Engineer',
    'Backend Developer',
    'Systems Architect',
    'Machine Learning Engineer',
    'Competitive Programmer',
    'Technical Lead',
    'Principal Engineer',
    'Research Scientist',
    'Performance Engineer'
  ],
  industryApplications: [
    'Software Development',
    'Web and Mobile Applications',
    'Database Management Systems',
    'Search Engines',
    'Operating Systems',
    'Game Development',
    'Network Protocols',
    'Machine Learning',
    'Cybersecurity',
    'Financial Systems',
    'Scientific Computing',
    'Distributed Systems'
  ],
  modules: [
    {
      id: 'arrays-linked-lists',
      title: 'Arrays and Linked Lists',
      duration: '3 weeks',
      learningObjectives: [
        'Understand array operations and their complexity',
        'Implement various types of linked lists',
        'Compare array vs linked list trade-offs',
        'Solve problems using arrays and linked lists'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Array Data Structure and Operations',
          content: `# Arrays and Linked Lists

## Array Data Structure

Arrays are fundamental data structures that store elements in contiguous memory locations, allowing random access to elements using indices.

### Array Characteristics
- **Fixed Size**: Traditional arrays have a predetermined size
- **Homogeneous**: All elements are of the same data type
- **Index-based Access**: O(1) random access using indices
- **Memory Efficient**: Minimal memory overhead

### Array Operations and Complexity

| Operation | Time Complexity | Description |
|-----------|----------------|-------------|
| Access | O(1) | Direct index access |
| Search | O(n) | Linear search through elements |
| Insertion | O(n) | Shifting elements required |
| Deletion | O(n) | Shifting elements required |
| Append | O(1) | Adding to end (if space available) |

### Dynamic Arrays
Dynamic arrays (like JavaScript arrays, Python lists) can resize automatically:

\`\`\`javascript
class DynamicArray {
  constructor(initialCapacity = 4) {
    this.capacity = initialCapacity;
    this.size = 0;
    this.data = new Array(this.capacity);
  }
  
  // Get element at index
  get(index) {
    if (index < 0 || index >= this.size) {
      throw new Error('Index out of bounds');
    }
    return this.data[index];
  }
  
  // Set element at index
  set(index, value) {
    if (index < 0 || index >= this.size) {
      throw new Error('Index out of bounds');
    }
    this.data[index] = value;
  }
  
  // Add element to end
  push(value) {
    if (this.size >= this.capacity) {
      this.resize();
    }
    this.data[this.size] = value;
    this.size++;
  }
  
  // Remove and return last element
  pop() {
    if (this.size === 0) {
      throw new Error('Array is empty');
    }
    const value = this.data[this.size - 1];
    this.size--;
    return value;
  }
  
  // Insert at specific index
  insert(index, value) {
    if (index < 0 || index > this.size) {
      throw new Error('Index out of bounds');
    }
    
    if (this.size >= this.capacity) {
      this.resize();
    }
    
    // Shift elements to the right
    for (let i = this.size; i > index; i--) {
      this.data[i] = this.data[i - 1];
    }
    
    this.data[index] = value;
    this.size++;
  }
  
  // Delete element at index
  delete(index) {
    if (index < 0 || index >= this.size) {
      throw new Error('Index out of bounds');
    }
    
    const value = this.data[index];
    
    // Shift elements to the left
    for (let i = index; i < this.size - 1; i++) {
      this.data[i] = this.data[i + 1];
    }
    
    this.size--;
    return value;
  }
  
  // Resize array when capacity is exceeded
  resize() {
    const oldData = this.data;
    this.capacity *= 2;
    this.data = new Array(this.capacity);
    
    for (let i = 0; i < this.size; i++) {
      this.data[i] = oldData[i];
    }
  }
  
  // Get current size
  length() {
    return this.size;
  }
  
  // Check if array is empty
  isEmpty() {
    return this.size === 0;
  }
}
\`\`\`

## Linked Lists

Linked lists are linear data structures where elements are stored in nodes, and each node contains data and a reference to the next node.

### Types of Linked Lists

#### Singly Linked List
Each node points to the next node in the sequence.

\`\`\`javascript
class ListNode {
  constructor(data) {
    this.data = data;
    this.next = null;
  }
}

class SinglyLinkedList {
  constructor() {
    this.head = null;
    this.size = 0;
  }
  
  // Add element to beginning
  prepend(data) {
    const newNode = new ListNode(data);
    newNode.next = this.head;
    this.head = newNode;
    this.size++;
  }
  
  // Add element to end
  append(data) {
    const newNode = new ListNode(data);
    
    if (!this.head) {
      this.head = newNode;
    } else {
      let current = this.head;
      while (current.next) {
        current = current.next;
      }
      current.next = newNode;
    }
    this.size++;
  }
  
  // Insert at specific index
  insertAt(index, data) {
    if (index < 0 || index > this.size) {
      throw new Error('Index out of bounds');
    }
    
    if (index === 0) {
      this.prepend(data);
      return;
    }
    
    const newNode = new ListNode(data);
    let current = this.head;
    
    for (let i = 0; i < index - 1; i++) {
      current = current.next;
    }
    
    newNode.next = current.next;
    current.next = newNode;
    this.size++;
  }
  
  // Remove element by value
  remove(data) {
    if (!this.head) return false;
    
    if (this.head.data === data) {
      this.head = this.head.next;
      this.size--;
      return true;
    }
    
    let current = this.head;
    while (current.next && current.next.data !== data) {
      current = current.next;
    }
    
    if (current.next) {
      current.next = current.next.next;
      this.size--;
      return true;
    }
    
    return false;
  }
  
  // Find element and return index
  indexOf(data) {
    let current = this.head;
    let index = 0;
    
    while (current) {
      if (current.data === data) {
        return index;
      }
      current = current.next;
      index++;
    }
    
    return -1;
  }
  
  // Get element at index
  get(index) {
    if (index < 0 || index >= this.size) {
      throw new Error('Index out of bounds');
    }
    
    let current = this.head;
    for (let i = 0; i < index; i++) {
      current = current.next;
    }
    
    return current.data;
  }
  
  // Convert to array for display
  toArray() {
    const result = [];
    let current = this.head;
    
    while (current) {
      result.push(current.data);
      current = current.next;
    }
    
    return result;
  }
}
\`\`\`

#### Doubly Linked List
Each node has references to both next and previous nodes.

\`\`\`javascript
class DoublyListNode {
  constructor(data) {
    this.data = data;
    this.next = null;
    this.prev = null;
  }
}

class DoublyLinkedList {
  constructor() {
    this.head = null;
    this.tail = null;
    this.size = 0;
  }
  
  // Add to beginning
  prepend(data) {
    const newNode = new DoublyListNode(data);
    
    if (!this.head) {
      this.head = this.tail = newNode;
    } else {
      newNode.next = this.head;
      this.head.prev = newNode;
      this.head = newNode;
    }
    this.size++;
  }
  
  // Add to end
  append(data) {
    const newNode = new DoublyListNode(data);
    
    if (!this.tail) {
      this.head = this.tail = newNode;
    } else {
      this.tail.next = newNode;
      newNode.prev = this.tail;
      this.tail = newNode;
    }
    this.size++;
  }
  
  // Remove from beginning
  removeFirst() {
    if (!this.head) return null;
    
    const data = this.head.data;
    
    if (this.head === this.tail) {
      this.head = this.tail = null;
    } else {
      this.head = this.head.next;
      this.head.prev = null;
    }
    
    this.size--;
    return data;
  }
  
  // Remove from end
  removeLast() {
    if (!this.tail) return null;
    
    const data = this.tail.data;
    
    if (this.head === this.tail) {
      this.head = this.tail = null;
    } else {
      this.tail = this.tail.prev;
      this.tail.next = null;
    }
    
    this.size--;
    return data;
  }
}
\`\`\`

## Array vs Linked List Comparison

| Aspect | Array | Linked List |
|--------|-------|-------------|
| Memory Layout | Contiguous | Non-contiguous |
| Access Time | O(1) random access | O(n) sequential access |
| Insertion/Deletion | O(n) (middle) | O(1) if node known |
| Memory Overhead | Low | High (pointers) |
| Cache Performance | Excellent | Poor |
| Use Cases | Frequent access, math operations | Frequent insertions/deletions |

## Applications

### Arrays
- Mathematical computations and matrices
- Database storage and indexing
- Image and signal processing
- Implementing other data structures

### Linked Lists
- Implementation of stacks and queues
- Undo functionality in applications
- Music playlists and navigation systems
- Memory management in operating systems`
        },
        {
          type: 'interactive_exercise',
          title: 'Array and Linked List Implementation Challenge',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Implement a comprehensive array and linked list toolkit:

1. Create a DynamicArray class with all basic operations
2. Implement a DoublyLinkedList with insertion, deletion, and search
3. Build a performance comparison tool between arrays and linked lists
4. Solve the "Remove Duplicates" problem for both structures
5. Implement array rotation and linked list reversal algorithms

Your implementation should handle:
- Dynamic resizing for arrays
- Efficient insertion/deletion for linked lists
- Edge cases (empty structures, boundary indices)
- Performance analysis and comparison
- Common algorithmic problems

Test with: array operations, linked list manipulations, and performance benchmarks.`,
            initialCode: `class DynamicArray {
  constructor(initialCapacity = 4) {
    // Implement dynamic array with automatic resizing
  }
  
  push(value) {
    // Add element to end, resize if needed
  }
  
  insert(index, value) {
    // Insert at specific index
  }
  
  delete(index) {
    // Remove element at index
  }
  
  removeDuplicates() {
    // Remove duplicate elements efficiently
  }
  
  rotateLeft(k) {
    // Rotate array left by k positions
  }
}

class DoublyLinkedList {
  constructor() {
    // Initialize doubly linked list
  }
  
  append(data) {
    // Add to end of list
  }
  
  prepend(data) {
    // Add to beginning of list
  }
  
  removeValue(data) {
    // Remove first occurrence of value
  }
  
  reverse() {
    // Reverse the entire list
  }
  
  removeDuplicates() {
    // Remove duplicate values
  }
}

class PerformanceComparator {
  compareInsertions(arraySize, insertions) {
    // Compare insertion performance between array and list
    // Return: { arrayTime: number, listTime: number, ratio: number }
  }
  
  compareAccess(size, accessCount) {
    // Compare random access performance
    // Return performance metrics
  }
}

// Test implementations
const dynArray = new DynamicArray();
const doublyList = new DoublyLinkedList();
const comparator = new PerformanceComparator();

// Test array operations
dynArray.push(1);
dynArray.push(2);
dynArray.push(2);
dynArray.push(3);
console.log("Before removing duplicates:", dynArray.toArray());
dynArray.removeDuplicates();
console.log("After removing duplicates:", dynArray.toArray());

// Test linked list operations  
doublyList.append(1);
doublyList.append(2);
doublyList.prepend(0);
console.log("List contents:", doublyList.toArray());
doublyList.reverse();
console.log("Reversed list:", doublyList.toArray());

// Performance comparison
const comparison = comparator.compareInsertions(1000, 500);
console.log("Insertion performance ratio (array/list):", comparison.ratio);`,
            expectedOutput: `Before removing duplicates: [1, 2, 2, 3]
After removing duplicates: [1, 2, 3]
List contents: [0, 1, 2]
Reversed list: [2, 1, 0]
Insertion performance ratio (array/list): 0.85`,
            hints: [
              'Dynamic array: double capacity when full, use modulo for rotation',
              'Doubly linked list: maintain both head and tail pointers',
              'Remove duplicates: use Set for arrays, traverse once for lists',
              'Reverse linked list: swap next and prev pointers for each node',
              'Performance: arrays are faster for access, lists for insertion/deletion',
              'Handle edge cases: empty structures, single elements, out of bounds'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Data Structure Implementation',
          title: 'Array and Linked List Mastery Project',
          points: 100,
          dueWeek: 3
        }
      ]
    },
    {
      id: 'stacks-queues',
      title: 'Stacks and Queues',
      duration: '2 weeks',
      learningObjectives: [
        'Implement stack and queue data structures',
        'Apply stacks and queues to solve real-world problems',
        'Understand LIFO and FIFO principles',
        'Use stacks for recursion and expression evaluation'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Stack and Queue Implementation',
          content: `# Stacks and Queues

## Stack Data Structure

A stack is a linear data structure that follows the Last In, First Out (LIFO) principle. Elements are added and removed from the same end, called the "top" of the stack.

### Stack Operations
- **Push**: Add element to top
- **Pop**: Remove and return top element
- **Peek/Top**: View top element without removing
- **isEmpty**: Check if stack is empty
- **Size**: Get number of elements

### Stack Implementation Using Array

\`\`\`javascript
class Stack {
  constructor() {
    this.items = [];
  }
  
  // Add element to top of stack
  push(element) {
    this.items.push(element);
  }
  
  // Remove and return top element
  pop() {
    if (this.isEmpty()) {
      throw new Error('Stack is empty');
    }
    return this.items.pop();
  }
  
  // View top element without removing
  peek() {
    if (this.isEmpty()) {
      throw new Error('Stack is empty');
    }
    return this.items[this.items.length - 1];
  }
  
  // Check if stack is empty
  isEmpty() {
    return this.items.length === 0;
  }
  
  // Get stack size
  size() {
    return this.items.length;
  }
  
  // Clear all elements
  clear() {
    this.items = [];
  }
  
  // Convert to array for display
  toArray() {
    return [...this.items];
  }
}
\`\`\`

### Stack Implementation Using Linked List

\`\`\`javascript
class StackNode {
  constructor(data) {
    this.data = data;
    this.next = null;
  }
}

class LinkedStack {
  constructor() {
    this.top = null;
    this.length = 0;
  }
  
  push(data) {
    const newNode = new StackNode(data);
    newNode.next = this.top;
    this.top = newNode;
    this.length++;
  }
  
  pop() {
    if (!this.top) {
      throw new Error('Stack is empty');
    }
    
    const data = this.top.data;
    this.top = this.top.next;
    this.length--;
    return data;
  }
  
  peek() {
    if (!this.top) {
      throw new Error('Stack is empty');
    }
    return this.top.data;
  }
  
  isEmpty() {
    return this.top === null;
  }
  
  size() {
    return this.length;
  }
}
\`\`\`

### Stack Applications

#### 1. Expression Evaluation
\`\`\`javascript
class ExpressionEvaluator {
  // Check for balanced parentheses
  static isBalanced(expression) {
    const stack = new Stack();
    const pairs = {
      '(': ')',
      '[': ']',
      '{': '}'
    };
    
    for (let char of expression) {
      if (pairs[char]) {
        stack.push(char);
      } else if (Object.values(pairs).includes(char)) {
        if (stack.isEmpty()) return false;
        const last = stack.pop();
        if (pairs[last] !== char) return false;
      }
    }
    
    return stack.isEmpty();
  }
  
  // Convert infix to postfix notation
  static infixToPostfix(infix) {
    const stack = new Stack();
    const result = [];
    const precedence = { '+': 1, '-': 1, '*': 2, '/': 2, '^': 3 };
    const rightAssociative = { '^': true };
    
    for (let token of infix.split(' ')) {
      if (/\\d/.test(token)) {
        result.push(token);
      } else if (token === '(') {
        stack.push(token);
      } else if (token === ')') {
        while (!stack.isEmpty() && stack.peek() !== '(') {
          result.push(stack.pop());
        }
        stack.pop(); // Remove '('
      } else if (precedence[token]) {
        while (
          !stack.isEmpty() &&
          precedence[stack.peek()] &&
          (precedence[stack.peek()] > precedence[token] ||
           (precedence[stack.peek()] === precedence[token] && !rightAssociative[token]))
        ) {
          result.push(stack.pop());
        }
        stack.push(token);
      }
    }
    
    while (!stack.isEmpty()) {
      result.push(stack.pop());
    }
    
    return result.join(' ');
  }
  
  // Evaluate postfix expression
  static evaluatePostfix(postfix) {
    const stack = new Stack();
    
    for (let token of postfix.split(' ')) {
      if (/\\d/.test(token)) {
        stack.push(parseFloat(token));
      } else {
        const b = stack.pop();
        const a = stack.pop();
        
        switch (token) {
          case '+': stack.push(a + b); break;
          case '-': stack.push(a - b); break;
          case '*': stack.push(a * b); break;
          case '/': stack.push(a / b); break;
          case '^': stack.push(Math.pow(a, b)); break;
        }
      }
    }
    
    return stack.pop();
  }
}
\`\`\`

## Queue Data Structure

A queue is a linear data structure that follows the First In, First Out (FIFO) principle. Elements are added at the rear and removed from the front.

### Queue Operations
- **Enqueue**: Add element to rear
- **Dequeue**: Remove and return front element
- **Front**: View front element without removing
- **Rear**: View rear element without removing
- **isEmpty**: Check if queue is empty
- **Size**: Get number of elements

### Queue Implementation Using Array

\`\`\`javascript
class Queue {
  constructor() {
    this.items = [];
  }
  
  // Add element to rear of queue
  enqueue(element) {
    this.items.push(element);
  }
  
  // Remove and return front element
  dequeue() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items.shift();
  }
  
  // View front element
  front() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items[0];
  }
  
  // View rear element
  rear() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items[this.items.length - 1];
  }
  
  isEmpty() {
    return this.items.length === 0;
  }
  
  size() {
    return this.items.length;
  }
  
  clear() {
    this.items = [];
  }
  
  toArray() {
    return [...this.items];
  }
}
\`\`\`

### Circular Queue Implementation

\`\`\`javascript
class CircularQueue {
  constructor(capacity) {
    this.capacity = capacity;
    this.items = new Array(capacity);
    this.front = 0;
    this.rear = -1;
    this.count = 0;
  }
  
  enqueue(element) {
    if (this.isFull()) {
      throw new Error('Queue is full');
    }
    
    this.rear = (this.rear + 1) % this.capacity;
    this.items[this.rear] = element;
    this.count++;
  }
  
  dequeue() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    
    const element = this.items[this.front];
    this.items[this.front] = undefined;
    this.front = (this.front + 1) % this.capacity;
    this.count--;
    return element;
  }
  
  peek() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items[this.front];
  }
  
  isEmpty() {
    return this.count === 0;
  }
  
  isFull() {
    return this.count === this.capacity;
  }
  
  size() {
    return this.count;
  }
}
\`\`\`

### Priority Queue Implementation

\`\`\`javascript
class PriorityQueue {
  constructor(compareFunction) {
    this.items = [];
    this.compare = compareFunction || ((a, b) => a.priority - b.priority);
  }
  
  enqueue(element, priority) {
    const queueElement = { element, priority };
    let added = false;
    
    for (let i = 0; i < this.items.length; i++) {
      if (this.compare(queueElement, this.items[i]) < 0) {
        this.items.splice(i, 0, queueElement);
        added = true;
        break;
      }
    }
    
    if (!added) {
      this.items.push(queueElement);
    }
  }
  
  dequeue() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items.shift().element;
  }
  
  front() {
    if (this.isEmpty()) {
      throw new Error('Queue is empty');
    }
    return this.items[0].element;
  }
  
  isEmpty() {
    return this.items.length === 0;
  }
  
  size() {
    return this.items.length;
  }
}
\`\`\`

### Queue Applications

#### 1. Breadth-First Search (BFS)
\`\`\`javascript
class Graph {
  constructor() {
    this.adjacencyList = new Map();
  }
  
  addVertex(vertex) {
    if (!this.adjacencyList.has(vertex)) {
      this.adjacencyList.set(vertex, []);
    }
  }
  
  addEdge(vertex1, vertex2) {
    this.adjacencyList.get(vertex1).push(vertex2);
    this.adjacencyList.get(vertex2).push(vertex1);
  }
  
  bfs(startVertex) {
    const visited = new Set();
    const queue = new Queue();
    const result = [];
    
    queue.enqueue(startVertex);
    visited.add(startVertex);
    
    while (!queue.isEmpty()) {
      const currentVertex = queue.dequeue();
      result.push(currentVertex);
      
      const neighbors = this.adjacencyList.get(currentVertex);
      for (let neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.enqueue(neighbor);
        }
      }
    }
    
    return result;
  }
}
\`\`\`

## Stack vs Queue Comparison

| Aspect | Stack | Queue |
|--------|-------|-------|
| Principle | LIFO | FIFO |
| Insertion | Top (push) | Rear (enqueue) |
| Removal | Top (pop) | Front (dequeue) |
| Use Cases | Function calls, undo operations | Task scheduling, BFS |
| Memory | Simple array/linked list | May need circular buffer |

## Real-World Applications

### Stacks
- Function call management
- Undo/Redo operations
- Browser history
- Expression parsing
- Compiler syntax checking

### Queues
- Process scheduling in OS
- Print job management
- Network packet handling
- Breadth-first search
- Customer service systems`
        },
        {
          type: 'interactive_exercise',
          title: 'Stack and Queue Problem Solver',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive stack and queue problem-solving toolkit:

1. Implement Stack and Queue classes with all operations
2. Create an expression evaluator using stacks (infix to postfix conversion)
3. Build a task scheduler using priority queues
4. Implement browser history simulation using stacks
5. Create a BFS pathfinder using queues

Your implementation should handle:
- Balanced parentheses checking
- Mathematical expression evaluation
- Priority-based task management
- Graph traversal algorithms
- Real-world simulation scenarios

Test with: complex expressions, task scheduling, and graph navigation.`,
            initialCode: `class AdvancedStack {
  constructor() {
    this.items = [];
  }
  
  push(element) {
    // Implement stack push
  }
  
  pop() {
    // Implement stack pop
  }
  
  peek() {
    // View top element
  }
  
  // Additional methods for problem solving
  isEmpty() {
    return this.items.length === 0;
  }
}

class AdvancedQueue {
  constructor() {
    this.items = [];
  }
  
  enqueue(element) {
    // Implement queue enqueue
  }
  
  dequeue() {
    // Implement queue dequeue
  }
  
  front() {
    // View front element
  }
}

class ExpressionValidator {
  static isValidParentheses(expression) {
    // Check if parentheses are balanced
    // Handle: (), [], {}
    // Return: boolean
  }
  
  static evaluatePostfix(expression) {
    // Evaluate postfix mathematical expression
    // Handle: +, -, *, /, ^
    // Return: numeric result
  }
  
  static infixToPostfix(expression) {
    // Convert infix to postfix notation
    // Handle operator precedence and associativity
    // Return: postfix string
  }
}

class TaskScheduler {
  constructor() {
    this.tasks = [];
  }
  
  addTask(task, priority) {
    // Add task with priority (lower number = higher priority)
    // Maintain sorted order by priority
  }
  
  executeNext() {
    // Execute highest priority task
    // Return: executed task or null if empty
  }
  
  getAllTasks() {
    // Return all pending tasks sorted by priority
  }
}

class BrowserHistory {
  constructor() {
    this.history = new AdvancedStack();
    this.forward = new AdvancedStack();
  }
  
  visit(url) {
    // Visit new URL, clear forward history
  }
  
  back() {
    // Go back one page
    // Return: previous URL or null
  }
  
  forward() {
    // Go forward one page  
    // Return: next URL or null
  }
  
  getCurrentUrl() {
    // Get current page URL
  }
}

// Test implementations
console.log("Testing Expression Validator:");
console.log("Valid parentheses:", ExpressionValidator.isValidParentheses("(({}[]))"));
console.log("Invalid parentheses:", ExpressionValidator.isValidParentheses("([)]"));

console.log("\\nTesting Postfix Evaluation:");
const postfix = "3 4 + 2 * 7 /";
console.log(\`\${postfix} = \${ExpressionValidator.evaluatePostfix(postfix)}\`);

console.log("\\nTesting Task Scheduler:");
const scheduler = new TaskScheduler();
scheduler.addTask("Send email", 2);
scheduler.addTask("Fix critical bug", 1);
scheduler.addTask("Update documentation", 3);
console.log("Next task:", scheduler.executeNext());
console.log("Remaining tasks:", scheduler.getAllTasks());

console.log("\\nTesting Browser History:");
const browser = new BrowserHistory();
browser.visit("google.com");
browser.visit("github.com");
browser.visit("stackoverflow.com");
console.log("Current:", browser.getCurrentUrl());
console.log("Back to:", browser.back());
console.log("Forward to:", browser.forward());`,
            expectedOutput: `Testing Expression Validator:
Valid parentheses: true
Invalid parentheses: false

Testing Postfix Evaluation:
3 4 + 2 * 7 / = 2

Testing Task Scheduler:
Next task: Fix critical bug
Remaining tasks: [Send email, Update documentation]

Testing Browser History:
Current: stackoverflow.com
Back to: github.com
Forward to: stackoverflow.com`,
            hints: [
              'Balanced parentheses: use stack to match opening/closing pairs',
              'Postfix evaluation: operands go on stack, operators pop two values',
              'Task scheduler: maintain sorted array by priority or use heap',
              'Browser history: use two stacks for back/forward navigation',
              'Expression conversion: handle operator precedence and associativity',
              'Test edge cases: empty inputs, single elements, full capacity'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Stack and Queue Applications',
          title: 'Advanced Problem Solving with Linear Structures',
          points: 75,
          dueWeek: 5
        }
      ]
    },
    {
      id: 'trees-graphs',
      title: 'Trees and Graph Algorithms',
      duration: '4 weeks',
      learningObjectives: [
        'Implement various tree data structures (binary trees, BST, AVL, heaps)',
        'Master tree traversal algorithms (in-order, pre-order, post-order, level-order)',
        'Understand graph representations and implement graph algorithms',
        'Apply BFS, DFS, and shortest path algorithms'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Binary Trees and Tree Traversals',
          content: `# Trees and Graph Algorithms

## Tree Data Structure Fundamentals

Trees are hierarchical data structures consisting of nodes connected by edges. Each tree has a root node, and every other node has exactly one parent.

### Tree Terminology
- **Root**: Top node with no parent
- **Parent**: Node with children
- **Child**: Node with a parent
- **Leaf**: Node with no children
- **Height**: Length of longest path from root to leaf
- **Depth**: Length of path from root to node
- **Subtree**: Tree rooted at any node

### Binary Tree Implementation

\`\`\`javascript
class TreeNode {
  constructor(data) {
    this.data = data;
    this.left = null;
    this.right = null;
  }
}

class BinaryTree {
  constructor() {
    this.root = null;
  }
  
  // Insert node (level-order insertion for complete tree)
  insert(data) {
    const newNode = new TreeNode(data);
    
    if (!this.root) {
      this.root = newNode;
      return;
    }
    
    const queue = [this.root];
    
    while (queue.length > 0) {
      const current = queue.shift();
      
      if (!current.left) {
        current.left = newNode;
        return;
      } else if (!current.right) {
        current.right = newNode;
        return;
      } else {
        queue.push(current.left);
        queue.push(current.right);
      }
    }
  }
  
  // Find node with given data
  search(data, node = this.root) {
    if (!node) return null;
    if (node.data === data) return node;
    
    const leftResult = this.search(data, node.left);
    if (leftResult) return leftResult;
    
    return this.search(data, node.right);
  }
  
  // Get tree height
  getHeight(node = this.root) {
    if (!node) return -1;
    
    const leftHeight = this.getHeight(node.left);
    const rightHeight = this.getHeight(node.right);
    
    return 1 + Math.max(leftHeight, rightHeight);
  }
  
  // Count total nodes
  countNodes(node = this.root) {
    if (!node) return 0;
    return 1 + this.countNodes(node.left) + this.countNodes(node.right);
  }
  
  // Check if tree is balanced
  isBalanced(node = this.root) {
    if (!node) return true;
    
    const leftHeight = this.getHeight(node.left);
    const rightHeight = this.getHeight(node.right);
    
    return Math.abs(leftHeight - rightHeight) <= 1 &&
           this.isBalanced(node.left) &&
           this.isBalanced(node.right);
  }
}
\`\`\`

### Tree Traversal Algorithms

#### Depth-First Traversals

\`\`\`javascript
class TreeTraversal {
  // In-order traversal (Left, Root, Right)
  static inOrder(node, result = []) {
    if (node) {
      TreeTraversal.inOrder(node.left, result);
      result.push(node.data);
      TreeTraversal.inOrder(node.right, result);
    }
    return result;
  }
  
  // Pre-order traversal (Root, Left, Right)
  static preOrder(node, result = []) {
    if (node) {
      result.push(node.data);
      TreeTraversal.preOrder(node.left, result);
      TreeTraversal.preOrder(node.right, result);
    }
    return result;
  }
  
  // Post-order traversal (Left, Right, Root)
  static postOrder(node, result = []) {
    if (node) {
      TreeTraversal.postOrder(node.left, result);
      TreeTraversal.postOrder(node.right, result);
      result.push(node.data);
    }
    return result;
  }
  
  // Iterative in-order traversal
  static inOrderIterative(root) {
    const result = [];
    const stack = [];
    let current = root;
    
    while (current || stack.length > 0) {
      while (current) {
        stack.push(current);
        current = current.left;
      }
      
      current = stack.pop();
      result.push(current.data);
      current = current.right;
    }
    
    return result;
  }
  
  // Level-order traversal (BFS)
  static levelOrder(root) {
    if (!root) return [];
    
    const result = [];
    const queue = [root];
    
    while (queue.length > 0) {
      const levelSize = queue.length;
      const currentLevel = [];
      
      for (let i = 0; i < levelSize; i++) {
        const node = queue.shift();
        currentLevel.push(node.data);
        
        if (node.left) queue.push(node.left);
        if (node.right) queue.push(node.right);
      }
      
      result.push(currentLevel);
    }
    
    return result;
  }
}
\`\`\`

### Binary Search Tree (BST)

\`\`\`javascript
class BinarySearchTree {
  constructor() {
    this.root = null;
  }
  
  // Insert node maintaining BST property
  insert(data) {
    this.root = this.insertNode(this.root, data);
  }
  
  insertNode(node, data) {
    if (!node) {
      return new TreeNode(data);
    }
    
    if (data < node.data) {
      node.left = this.insertNode(node.left, data);
    } else if (data > node.data) {
      node.right = this.insertNode(node.right, data);
    }
    
    return node;
  }
  
  // Search for a value
  search(data) {
    return this.searchNode(this.root, data);
  }
  
  searchNode(node, data) {
    if (!node) return false;
    if (data === node.data) return true;
    
    if (data < node.data) {
      return this.searchNode(node.left, data);
    } else {
      return this.searchNode(node.right, data);
    }
  }
  
  // Find minimum value
  findMin(node = this.root) {
    if (!node) return null;
    
    while (node.left) {
      node = node.left;
    }
    
    return node.data;
  }
  
  // Find maximum value
  findMax(node = this.root) {
    if (!node) return null;
    
    while (node.right) {
      node = node.right;
    }
    
    return node.data;
  }
  
  // Delete node
  delete(data) {
    this.root = this.deleteNode(this.root, data);
  }
  
  deleteNode(node, data) {
    if (!node) return null;
    
    if (data < node.data) {
      node.left = this.deleteNode(node.left, data);
    } else if (data > node.data) {
      node.right = this.deleteNode(node.right, data);
    } else {
      // Node to delete found
      
      // Case 1: No children (leaf node)
      if (!node.left && !node.right) {
        return null;
      }
      
      // Case 2: One child
      if (!node.left) return node.right;
      if (!node.right) return node.left;
      
      // Case 3: Two children
      // Find inorder successor (minimum in right subtree)
      const minValue = this.findMin(node.right);
      node.data = minValue;
      node.right = this.deleteNode(node.right, minValue);
    }
    
    return node;
  }
  
  // Validate BST property
  isValidBST(node = this.root, min = -Infinity, max = Infinity) {
    if (!node) return true;
    
    if (node.data <= min || node.data >= max) {
      return false;
    }
    
    return this.isValidBST(node.left, min, node.data) &&
           this.isValidBST(node.right, node.data, max);
  }
}
\`\`\`

## Graph Data Structure

Graphs consist of vertices (nodes) and edges connecting them. They can be directed or undirected, weighted or unweighted.

### Graph Representations

#### Adjacency List
\`\`\`javascript
class Graph {
  constructor(isDirected = false) {
    this.isDirected = isDirected;
    this.vertices = new Map();
  }
  
  addVertex(vertex) {
    if (!this.vertices.has(vertex)) {
      this.vertices.set(vertex, new Set());
    }
  }
  
  addEdge(vertex1, vertex2, weight = 1) {
    this.addVertex(vertex1);
    this.addVertex(vertex2);
    
    this.vertices.get(vertex1).add({vertex: vertex2, weight});
    
    if (!this.isDirected) {
      this.vertices.get(vertex2).add({vertex: vertex1, weight});
    }
  }
  
  removeVertex(vertex) {
    if (!this.vertices.has(vertex)) return;
    
    // Remove all edges to this vertex
    for (let [v, edges] of this.vertices) {
      for (let edge of edges) {
        if (edge.vertex === vertex) {
          edges.delete(edge);
        }
      }
    }
    
    // Remove the vertex itself
    this.vertices.delete(vertex);
  }
  
  removeEdge(vertex1, vertex2) {
    if (this.vertices.has(vertex1)) {
      for (let edge of this.vertices.get(vertex1)) {
        if (edge.vertex === vertex2) {
          this.vertices.get(vertex1).delete(edge);
          break;
        }
      }
    }
    
    if (!this.isDirected && this.vertices.has(vertex2)) {
      for (let edge of this.vertices.get(vertex2)) {
        if (edge.vertex === vertex1) {
          this.vertices.get(vertex2).delete(edge);
          break;
        }
      }
    }
  }
  
  getNeighbors(vertex) {
    return this.vertices.get(vertex) || new Set();
  }
  
  hasVertex(vertex) {
    return this.vertices.has(vertex);
  }
  
  hasEdge(vertex1, vertex2) {
    if (!this.vertices.has(vertex1)) return false;
    
    for (let edge of this.vertices.get(vertex1)) {
      if (edge.vertex === vertex2) return true;
    }
    
    return false;
  }
}
\`\`\`

### Graph Traversal Algorithms

#### Depth-First Search (DFS)
\`\`\`javascript
class GraphTraversal {
  // DFS - Recursive
  static dfsRecursive(graph, startVertex, visited = new Set(), result = []) {
    visited.add(startVertex);
    result.push(startVertex);
    
    const neighbors = graph.getNeighbors(startVertex);
    for (let edge of neighbors) {
      if (!visited.has(edge.vertex)) {
        GraphTraversal.dfsRecursive(graph, edge.vertex, visited, result);
      }
    }
    
    return result;
  }
  
  // DFS - Iterative
  static dfsIterative(graph, startVertex) {
    const visited = new Set();
    const stack = [startVertex];
    const result = [];
    
    while (stack.length > 0) {
      const vertex = stack.pop();
      
      if (!visited.has(vertex)) {
        visited.add(vertex);
        result.push(vertex);
        
        const neighbors = graph.getNeighbors(vertex);
        for (let edge of neighbors) {
          if (!visited.has(edge.vertex)) {
            stack.push(edge.vertex);
          }
        }
      }
    }
    
    return result;
  }
  
  // BFS
  static bfs(graph, startVertex) {
    const visited = new Set();
    const queue = [startVertex];
    const result = [];
    
    visited.add(startVertex);
    
    while (queue.length > 0) {
      const vertex = queue.shift();
      result.push(vertex);
      
      const neighbors = graph.getNeighbors(vertex);
      for (let edge of neighbors) {
        if (!visited.has(edge.vertex)) {
          visited.add(edge.vertex);
          queue.push(edge.vertex);
        }
      }
    }
    
    return result;
  }
  
  // Find path between two vertices
  static findPath(graph, startVertex, endVertex) {
    const visited = new Set();
    const queue = [{vertex: startVertex, path: [startVertex]}];
    
    while (queue.length > 0) {
      const {vertex, path} = queue.shift();
      
      if (vertex === endVertex) {
        return path;
      }
      
      if (!visited.has(vertex)) {
        visited.add(vertex);
        
        const neighbors = graph.getNeighbors(vertex);
        for (let edge of neighbors) {
          if (!visited.has(edge.vertex)) {
            queue.push({
              vertex: edge.vertex,
              path: [...path, edge.vertex]
            });
          }
        }
      }
    }
    
    return null; // No path found
  }
  
  // Check if graph is connected
  static isConnected(graph) {
    const vertices = Array.from(graph.vertices.keys());
    if (vertices.length === 0) return true;
    
    const visited = GraphTraversal.dfsRecursive(graph, vertices[0]);
    return visited.length === vertices.length;
  }
  
  // Detect cycle in undirected graph
  static hasCycleUndirected(graph) {
    const visited = new Set();
    
    for (let vertex of graph.vertices.keys()) {
      if (!visited.has(vertex)) {
        if (GraphTraversal.dfsHasCycle(graph, vertex, visited, null)) {
          return true;
        }
      }
    }
    
    return false;
  }
  
  static dfsHasCycle(graph, vertex, visited, parent) {
    visited.add(vertex);
    
    const neighbors = graph.getNeighbors(vertex);
    for (let edge of neighbors) {
      if (!visited.has(edge.vertex)) {
        if (GraphTraversal.dfsHasCycle(graph, edge.vertex, visited, vertex)) {
          return true;
        }
      } else if (edge.vertex !== parent) {
        return true; // Back edge found
      }
    }
    
    return false;
  }
}
\`\`\`

## Applications

### Trees
- File systems and directory structures
- Database indexing (B-trees)
- Expression parsing and evaluation
- Huffman coding for compression
- Decision trees in machine learning

### Graphs
- Social networks and relationship modeling
- Network routing and shortest paths
- Web page ranking (PageRank algorithm)
- Dependency resolution in build systems
- Game AI pathfinding algorithms`
        },
        {
          type: 'interactive_exercise',
          title: 'Tree and Graph Algorithm Implementation',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Implement a comprehensive tree and graph algorithm library:

1. Create a Binary Search Tree with all operations (insert, delete, search, traversals)
2. Build a Graph class supporting both directed and undirected graphs
3. Implement BFS and DFS traversal algorithms
4. Create a pathfinding algorithm to find shortest path between nodes
5. Build a tree validator to check if a binary tree is a valid BST

Your implementation should handle:
- BST operations with proper balancing consideration
- Graph representation using adjacency lists
- Multiple traversal algorithms with iterative and recursive versions
- Cycle detection in graphs
- Tree property validation

Test with: BST operations, graph construction, and pathfinding scenarios.`,
            initialCode: `class TreeNode {
  constructor(data) {
    this.data = data;
    this.left = null;
    this.right = null;
  }
}

class BinarySearchTree {
  constructor() {
    this.root = null;
  }
  
  insert(data) {
    // Insert maintaining BST property
  }
  
  search(data) {
    // Search for value in BST
  }
  
  delete(data) {
    // Delete node from BST
  }
  
  inOrder() {
    // Return in-order traversal array
  }
  
  isValidBST() {
    // Check if tree maintains BST property
    // Return: boolean
  }
  
  findKthLargest(k) {
    // Find kth largest element in BST
    // Return: value or null if k > tree size
  }
}

class Graph {
  constructor(isDirected = false) {
    this.isDirected = isDirected;
    this.adjacencyList = new Map();
  }
  
  addVertex(vertex) {
    // Add vertex to graph
  }
  
  addEdge(v1, v2, weight = 1) {
    // Add edge between vertices
  }
  
  bfs(startVertex) {
    // Breadth-first search traversal
    // Return: array of visited vertices in order
  }
  
  dfs(startVertex) {
    // Depth-first search traversal
    // Return: array of visited vertices in order
  }
  
  shortestPath(start, end) {
    // Find shortest path between two vertices
    // Return: array representing path, or null if no path
  }
  
  hasCycle() {
    // Detect if graph contains a cycle
    // Return: boolean
  }
  
  isConnected() {
    // Check if all vertices are reachable from any vertex
    // Return: boolean
  }
}

class TreeGraphAnalyzer {
  static compareBSTOperations(size) {
    // Compare insertion, search, deletion performance
    // Test with random vs sorted data
    // Return: performance metrics object
  }
  
  static findLongestPath(graph, startVertex) {
    // Find longest simple path from start vertex
    // Return: { path: array, length: number }
  }
  
  static isBipartite(graph) {
    // Check if graph can be colored with 2 colors
    // Return: boolean
  }
}

// Test implementations
console.log("Testing BST Operations:");
const bst = new BinarySearchTree();
[15, 10, 20, 8, 12, 25].forEach(val => bst.insert(val));
console.log("In-order traversal:", bst.inOrder());
console.log("Search 12:", bst.search(12));
console.log("Is valid BST:", bst.isValidBST());
console.log("3rd largest:", bst.findKthLargest(3));

console.log("\\nTesting Graph Operations:");
const graph = new Graph();
['A', 'B', 'C', 'D', 'E'].forEach(v => graph.addVertex(v));
graph.addEdge('A', 'B');
graph.addEdge('A', 'C');
graph.addEdge('B', 'D');
graph.addEdge('C', 'E');
graph.addEdge('D', 'E');

console.log("BFS from A:", graph.bfs('A'));
console.log("DFS from A:", graph.dfs('A'));
console.log("Path A to E:", graph.shortestPath('A', 'E'));
console.log("Has cycle:", graph.hasCycle());
console.log("Is connected:", graph.isConnected());`,
            expectedOutput: `Testing BST Operations:
In-order traversal: [8, 10, 12, 15, 20, 25]
Search 12: true
Is valid BST: true
3rd largest: 20

Testing Graph Operations:
BFS from A: ['A', 'B', 'C', 'D', 'E']
DFS from A: ['A', 'B', 'D', 'E', 'C']
Path A to E: ['A', 'C', 'E']
Has cycle: true
Is connected: true`,
            hints: [
              'BST insert: compare with current node, go left if smaller, right if larger',
              'BST delete: handle 3 cases - no children, one child, two children',
              'In-order traversal of BST gives sorted sequence',
              'Use BFS for shortest path in unweighted graphs',
              'DFS can be implemented recursively or with a stack',
              'Cycle detection: use DFS with visited and recursion stack tracking'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Tree and Graph Algorithms Project',
          title: 'Advanced Tree and Graph Problem Solving',
          points: 125,
          dueWeek: 9
        }
      ]
    },
    {
      id: 'sorting-searching',
      title: 'Sorting and Searching Algorithms',
      duration: '3 weeks',
      learningObjectives: [
        'Implement and analyze various sorting algorithms',
        'Understand searching techniques for different data structures',
        'Master divide-and-conquer algorithm design',
        'Apply optimization techniques for algorithm efficiency'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Sorting Algorithms and Analysis',
          content: `# Sorting and Searching Algorithms

## Sorting Algorithm Classifications

### Comparison-Based Sorting
Algorithms that sort by comparing elements:
- **Bubble Sort**: O(n²) - Simple but inefficient
- **Selection Sort**: O(n²) - Finds minimum/maximum iteratively
- **Insertion Sort**: O(n²) - Builds sorted portion incrementally
- **Merge Sort**: O(n log n) - Divide and conquer approach
- **Quick Sort**: O(n log n) average - Partition-based sorting
- **Heap Sort**: O(n log n) - Uses heap data structure

### Non-Comparison Sorting
Algorithms that don't rely on element comparisons:
- **Counting Sort**: O(n + k) - For small range of integers
- **Radix Sort**: O(d(n + k)) - Sorts by digit/character
- **Bucket Sort**: O(n + k) average - Distributes into buckets

## Elementary Sorting Algorithms

### Bubble Sort
\`\`\`javascript
class SortingAlgorithms {
  static bubbleSort(arr) {
    const n = arr.length;
    let swapped;
    
    for (let i = 0; i < n - 1; i++) {
      swapped = false;
      
      for (let j = 0; j < n - i - 1; j++) {
        if (arr[j] > arr[j + 1]) {
          // Swap elements
          [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
          swapped = true;
        }
      }
      
      // If no swapping occurred, array is sorted
      if (!swapped) break;
    }
    
    return arr;
  }
  
  // Optimized bubble sort with early termination
  static bubbleSortOptimized(arr) {
    let n = arr.length;
    
    do {
      let newN = 0;
      for (let i = 1; i < n; i++) {
        if (arr[i - 1] > arr[i]) {
          [arr[i - 1], arr[i]] = [arr[i], arr[i - 1]];
          newN = i;
        }
      }
      n = newN;
    } while (n > 1);
    
    return arr;
  }
}
\`\`\`

### Selection Sort
\`\`\`javascript
static selectionSort(arr) {
  const n = arr.length;
  
  for (let i = 0; i < n - 1; i++) {
    let minIndex = i;
    
    // Find minimum element in remaining array
    for (let j = i + 1; j < n; j++) {
      if (arr[j] < arr[minIndex]) {
        minIndex = j;
      }
    }
    
    // Swap minimum element with first element
    if (minIndex !== i) {
      [arr[i], arr[minIndex]] = [arr[minIndex], arr[i]];
    }
  }
  
  return arr;
}
\`\`\`

### Insertion Sort
\`\`\`javascript
static insertionSort(arr) {
  for (let i = 1; i < arr.length; i++) {
    let key = arr[i];
    let j = i - 1;
    
    // Move elements greater than key one position ahead
    while (j >= 0 && arr[j] > key) {
      arr[j + 1] = arr[j];
      j--;
    }
    
    arr[j + 1] = key;
  }
  
  return arr;
}

// Binary insertion sort - uses binary search for position
static binaryInsertionSort(arr) {
  for (let i = 1; i < arr.length; i++) {
    let key = arr[i];
    let left = 0;
    let right = i;
    
    // Binary search for insertion position
    while (left < right) {
      let mid = Math.floor((left + right) / 2);
      if (arr[mid] > key) {
        right = mid;
      } else {
        left = mid + 1;
      }
    }
    
    // Shift elements and insert
    for (let j = i; j > left; j--) {
      arr[j] = arr[j - 1];
    }
    arr[left] = key;
  }
  
  return arr;
}
\`\`\`

## Advanced Sorting Algorithms

### Merge Sort
\`\`\`javascript
static mergeSort(arr) {
  if (arr.length <= 1) return arr;
  
  const mid = Math.floor(arr.length / 2);
  const left = arr.slice(0, mid);
  const right = arr.slice(mid);
  
  return SortingAlgorithms.merge(
    SortingAlgorithms.mergeSort(left),
    SortingAlgorithms.mergeSort(right)
  );
}

static merge(left, right) {
  const result = [];
  let leftIndex = 0;
  let rightIndex = 0;
  
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] <= right[rightIndex]) {
      result.push(left[leftIndex]);
      leftIndex++;
    } else {
      result.push(right[rightIndex]);
      rightIndex++;
    }
  }
  
  // Add remaining elements
  return result.concat(left.slice(leftIndex)).concat(right.slice(rightIndex));
}

// In-place merge sort
static mergeSortInPlace(arr, left = 0, right = arr.length - 1) {
  if (left < right) {
    const mid = Math.floor((left + right) / 2);
    
    SortingAlgorithms.mergeSortInPlace(arr, left, mid);
    SortingAlgorithms.mergeSortInPlace(arr, mid + 1, right);
    SortingAlgorithms.mergeInPlace(arr, left, mid, right);
  }
  
  return arr;
}

static mergeInPlace(arr, left, mid, right) {
  const leftArr = arr.slice(left, mid + 1);
  const rightArr = arr.slice(mid + 1, right + 1);
  
  let i = 0, j = 0, k = left;
  
  while (i < leftArr.length && j < rightArr.length) {
    if (leftArr[i] <= rightArr[j]) {
      arr[k] = leftArr[i];
      i++;
    } else {
      arr[k] = rightArr[j];
      j++;
    }
    k++;
  }
  
  while (i < leftArr.length) {
    arr[k] = leftArr[i];
    i++;
    k++;
  }
  
  while (j < rightArr.length) {
    arr[k] = rightArr[j];
    j++;
    k++;
  }
}
\`\`\`

### Quick Sort
\`\`\`javascript
static quickSort(arr, low = 0, high = arr.length - 1) {
  if (low < high) {
    const pivotIndex = SortingAlgorithms.partition(arr, low, high);
    
    SortingAlgorithms.quickSort(arr, low, pivotIndex - 1);
    SortingAlgorithms.quickSort(arr, pivotIndex + 1, high);
  }
  
  return arr;
}

// Lomuto partition scheme
static partition(arr, low, high) {
  const pivot = arr[high];
  let i = low - 1;
  
  for (let j = low; j < high; j++) {
    if (arr[j] <= pivot) {
      i++;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return i + 1;
}

// Hoare partition scheme
static hoarePartition(arr, low, high) {
  const pivot = arr[low];
  let i = low - 1;
  let j = high + 1;
  
  while (true) {
    do {
      i++;
    } while (arr[i] < pivot);
    
    do {
      j--;
    } while (arr[j] > pivot);
    
    if (i >= j) return j;
    
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

// Quick sort with random pivot
static quickSortRandomized(arr, low = 0, high = arr.length - 1) {
  if (low < high) {
    // Random pivot selection
    const randomIndex = Math.floor(Math.random() * (high - low + 1)) + low;
    [arr[randomIndex], arr[high]] = [arr[high], arr[randomIndex]];
    
    const pivotIndex = SortingAlgorithms.partition(arr, low, high);
    
    SortingAlgorithms.quickSortRandomized(arr, low, pivotIndex - 1);
    SortingAlgorithms.quickSortRandomized(arr, pivotIndex + 1, high);
  }
  
  return arr;
}
\`\`\`

### Heap Sort
\`\`\`javascript
static heapSort(arr) {
  const n = arr.length;
  
  // Build max heap
  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) {
    SortingAlgorithms.heapify(arr, n, i);
  }
  
  // Extract elements from heap one by one
  for (let i = n - 1; i > 0; i--) {
    // Move current root to end
    [arr[0], arr[i]] = [arr[i], arr[0]];
    
    // Call heapify on reduced heap
    SortingAlgorithms.heapify(arr, i, 0);
  }
  
  return arr;
}

static heapify(arr, n, i) {
  let largest = i;
  let left = 2 * i + 1;
  let right = 2 * i + 2;
  
  // If left child is larger than root
  if (left < n && arr[left] > arr[largest]) {
    largest = left;
  }
  
  // If right child is larger than largest so far
  if (right < n && arr[right] > arr[largest]) {
    largest = right;
  }
  
  // If largest is not root
  if (largest !== i) {
    [arr[i], arr[largest]] = [arr[largest], arr[i]];
    
    // Recursively heapify the affected sub-tree
    SortingAlgorithms.heapify(arr, n, largest);
  }
}
\`\`\`

## Searching Algorithms

### Linear Search
\`\`\`javascript
class SearchingAlgorithms {
  static linearSearch(arr, target) {
    for (let i = 0; i < arr.length; i++) {
      if (arr[i] === target) {
        return i;
      }
    }
    return -1;
  }
  
  // Linear search with sentinel
  static linearSearchSentinel(arr, target) {
    const lastElement = arr[arr.length - 1];
    arr[arr.length - 1] = target;
    
    let i = 0;
    while (arr[i] !== target) {
      i++;
    }
    
    arr[arr.length - 1] = lastElement;
    
    if (i < arr.length - 1 || arr[arr.length - 1] === target) {
      return i;
    }
    
    return -1;
  }
}
\`\`\`

### Binary Search
\`\`\`javascript
static binarySearch(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    
    if (arr[mid] === target) {
      return mid;
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  
  return -1;
}

// Recursive binary search
static binarySearchRecursive(arr, target, left = 0, right = arr.length - 1) {
  if (left > right) return -1;
  
  const mid = Math.floor((left + right) / 2);
  
  if (arr[mid] === target) {
    return mid;
  } else if (arr[mid] < target) {
    return SearchingAlgorithms.binarySearchRecursive(arr, target, mid + 1, right);
  } else {
    return SearchingAlgorithms.binarySearchRecursive(arr, target, left, mid - 1);
  }
}

// Find first occurrence
static binarySearchFirst(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  let result = -1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    
    if (arr[mid] === target) {
      result = mid;
      right = mid - 1; // Continue searching in left half
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  
  return result;
}

// Find last occurrence
static binarySearchLast(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  let result = -1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    
    if (arr[mid] === target) {
      result = mid;
      left = mid + 1; // Continue searching in right half
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  
  return result;
}
\`\`\`

### Specialized Search Algorithms
\`\`\`javascript
// Interpolation search (for uniformly distributed data)
static interpolationSearch(arr, target) {
  let low = 0;
  let high = arr.length - 1;
  
  while (low <= high && target >= arr[low] && target <= arr[high]) {
    if (low === high) {
      return arr[low] === target ? low : -1;
    }
    
    // Interpolation formula
    const pos = low + Math.floor(
      ((target - arr[low]) / (arr[high] - arr[low])) * (high - low)
    );
    
    if (arr[pos] === target) {
      return pos;
    } else if (arr[pos] < target) {
      low = pos + 1;
    } else {
      high = pos - 1;
    }
  }
  
  return -1;
}

// Exponential search
static exponentialSearch(arr, target) {
  if (arr[0] === target) return 0;
  
  let i = 1;
  while (i < arr.length && arr[i] <= target) {
    i *= 2;
  }
  
  return SearchingAlgorithms.binarySearchRecursive(
    arr, target, i / 2, Math.min(i, arr.length - 1)
  );
}

// Ternary search (for finding maximum/minimum in unimodal function)
static ternarySearch(arr, left, right) {
  if (right - left < 3) {
    let maxIndex = left;
    for (let i = left + 1; i <= right; i++) {
      if (arr[i] > arr[maxIndex]) {
        maxIndex = i;
      }
    }
    return maxIndex;
  }
  
  const mid1 = left + Math.floor((right - left) / 3);
  const mid2 = right - Math.floor((right - left) / 3);
  
  if (arr[mid1] < arr[mid2]) {
    return SearchingAlgorithms.ternarySearch(arr, mid1, right);
  } else {
    return SearchingAlgorithms.ternarySearch(arr, left, mid2);
  }
}
\`\`\`

## Algorithm Complexity Analysis

| Algorithm | Best Case | Average Case | Worst Case | Space | Stable |
|-----------|-----------|--------------|------------|-------|--------|
| Bubble Sort | O(n) | O(n²) | O(n²) | O(1) | Yes |
| Selection Sort | O(n²) | O(n²) | O(n²) | O(1) | No |
| Insertion Sort | O(n) | O(n²) | O(n²) | O(1) | Yes |
| Merge Sort | O(n log n) | O(n log n) | O(n log n) | O(n) | Yes |
| Quick Sort | O(n log n) | O(n log n) | O(n²) | O(log n) | No |
| Heap Sort | O(n log n) | O(n log n) | O(n log n) | O(1) | No |

## When to Use Each Algorithm

### Bubble Sort
- Educational purposes only
- Very small datasets (< 10 elements)

### Selection Sort
- Small datasets
- When memory is limited
- When number of swaps must be minimized

### Insertion Sort
- Small datasets (< 50 elements)
- Nearly sorted data
- Online algorithm (can sort as data arrives)

### Merge Sort
- Large datasets
- When stable sort is required
- Linked lists
- External sorting

### Quick Sort
- General-purpose sorting
- When average-case performance matters
- In-place sorting needed

### Heap Sort
- When worst-case O(n log n) is required
- Memory-constrained environments
- Priority queue implementation`
        },
        {
          type: 'interactive_exercise',
          title: 'Sorting and Searching Performance Analyzer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive sorting and searching algorithm analyzer:

1. Implement all major sorting algorithms (bubble, selection, insertion, merge, quick, heap)
2. Build searching algorithms (linear, binary, interpolation, exponential)
3. Create a performance benchmarking system
4. Implement algorithm visualization and step counting
5. Build an algorithm recommender based on data characteristics

Your analyzer should:
- Compare time complexity in practice with different data sizes
- Handle different data distributions (random, sorted, reverse sorted)
- Provide step-by-step algorithm execution tracking
- Recommend optimal algorithm for given constraints
- Generate performance charts and statistics

Test with various datasets and analyze performance characteristics.`,
            initialCode: `class SortingAnalyzer {
  constructor() {
    this.algorithms = {
      bubble: this.bubbleSort.bind(this),
      selection: this.selectionSort.bind(this),
      insertion: this.insertionSort.bind(this),
      merge: this.mergeSort.bind(this),
      quick: this.quickSort.bind(this),
      heap: this.heapSort.bind(this)
    };
    this.stepCount = 0;
    this.comparisons = 0;
    this.swaps = 0;
  }
  
  // Implement sorting algorithms with step counting
  bubbleSort(arr) {
    // Implement with step counting for analysis
    // Track comparisons and swaps
  }
  
  mergeSort(arr) {
    // Implement divide-and-conquer with step tracking
  }
  
  quickSort(arr) {
    // Implement with partition strategy
  }
  
  // Benchmark multiple algorithms
  benchmarkSorts(dataSize, dataType = 'random') {
    // Test all algorithms with same dataset
    // dataType: 'random', 'sorted', 'reverse', 'nearly_sorted'
    // Return: { algorithm: string, time: number, comparisons: number, swaps: number }[]
  }
  
  // Generate test datasets
  generateData(size, type) {
    // Generate different types of test data
    // Return: array of specified size and distribution
  }
  
  // Recommend best algorithm
  recommendAlgorithm(dataSize, dataType, constraints = {}) {
    // constraints: { memory: 'limited', stability: true, worstCase: true }
    // Return: { algorithm: string, reason: string }
  }
}

class SearchingAnalyzer {
  constructor() {
    this.algorithms = {
      linear: this.linearSearch.bind(this),
      binary: this.binarySearch.bind(this),
      interpolation: this.interpolationSearch.bind(this),
      exponential: this.exponentialSearch.bind(this)
    };
  }
  
  linearSearch(arr, target) {
    // Implement with step counting
  }
  
  binarySearch(arr, target) {
    // Implement iterative binary search with step tracking
  }
  
  interpolationSearch(arr, target) {
    // Implement for uniformly distributed data
  }
  
  // Compare search performance
  benchmarkSearches(dataSize, searchCount = 100) {
    // Test search algorithms with multiple queries
    // Return performance comparison
  }
  
  // Find optimal search method
  recommendSearch(dataSize, dataDistribution, accessPattern) {
    // accessPattern: 'random', 'sequential', 'clustered'
    // Return best search algorithm with explanation
  }
}

class AlgorithmVisualizer {
  constructor() {
    this.steps = [];
  }
  
  // Visualize sorting algorithm execution
  visualizeSort(algorithm, data) {
    // Capture each step of sorting algorithm
    // Return: array of algorithm states for visualization
  }
  
  // Generate performance chart data
  generatePerformanceChart(algorithms, dataSizes) {
    // Test algorithms across different data sizes
    // Return: chart data showing time complexity curves
  }
}

// Test implementations
const sortAnalyzer = new SortingAnalyzer();
const searchAnalyzer = new SearchingAnalyzer();

// Benchmark sorting algorithms
console.log("Sorting Algorithm Benchmark (1000 elements):");
const sortResults = sortAnalyzer.benchmarkSorts(1000, 'random');
sortResults.forEach(result => {
  console.log(\`\${result.algorithm}: \${result.time}ms, \${result.comparisons} comparisons\`);
});

// Get algorithm recommendation
const recommendation = sortAnalyzer.recommendAlgorithm(10000, 'nearly_sorted', {
  stability: true,
  memory: 'limited'
});
console.log(\`\\nRecommended algorithm: \${recommendation.algorithm}\`);
console.log(\`Reason: \${recommendation.reason}\`);

// Search algorithm comparison
console.log("\\nSearch Algorithm Benchmark (10000 elements):");
const searchResults = searchAnalyzer.benchmarkSearches(10000);
console.log("Binary search avg steps:", searchResults.binary.avgSteps);
console.log("Linear search avg steps:", searchResults.linear.avgSteps);

// Search recommendation
const searchRec = searchAnalyzer.recommendSearch(50000, 'uniform', 'random');
console.log(\`Recommended search: \${searchRec.algorithm} - \${searchRec.reason}\`);`,
            expectedOutput: `Sorting Algorithm Benchmark (1000 elements):
bubble: 145ms, 249500 comparisons
insertion: 23ms, 125750 comparisons
merge: 12ms, 8684 comparisons
quick: 8ms, 7324 comparisons
heap: 15ms, 13816 comparisons

Recommended algorithm: insertion
Reason: Best for nearly sorted data with stability requirement

Search Algorithm Benchmark (10000 elements):
Binary search avg steps: 13.2
Linear search avg steps: 5000.5

Recommended search: interpolation - Optimal for uniform distribution with random access`,
            hints: [
              'Track comparisons and swaps separately in sorting algorithms',
              'Use performance.now() for accurate timing measurements',
              'Merge sort is stable and always O(n log n), good for large data',
              'Quick sort is fastest on average but O(n²) worst case',
              'Binary search requires sorted data, interpolation works best with uniform distribution',
              'Consider data characteristics when recommending algorithms'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Algorithm Analysis Project',
          title: 'Comprehensive Sorting and Searching Analysis',
          points: 100,
          dueWeek: 12
        }
      ]
    },
    {
      id: 'dynamic-programming',
      title: 'Dynamic Programming and Advanced Algorithms',
      duration: '3 weeks',
      learningObjectives: [
        'Understand dynamic programming principles and techniques',
        'Apply memoization and tabulation approaches',
        'Solve optimization problems using DP',
        'Master advanced algorithmic techniques'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Dynamic Programming Fundamentals',
          content: `# Dynamic Programming and Advanced Algorithms

## Dynamic Programming Principles

Dynamic Programming (DP) is an algorithmic technique for solving optimization problems by breaking them down into simpler subproblems. It works when the problem has:

1. **Optimal Substructure**: Optimal solution contains optimal solutions to subproblems
2. **Overlapping Subproblems**: Same subproblems are solved multiple times

### Two Main Approaches

#### 1. Memoization (Top-Down)
- Recursive approach with caching
- Store results of subproblems to avoid recomputation
- Natural recursion with added memory

#### 2. Tabulation (Bottom-Up)  
- Iterative approach building from base cases
- Fill table systematically from smaller to larger subproblems
- Often more space-efficient

## Classic DP Problems

### Fibonacci Sequence

#### Naive Recursive (Inefficient)
\`\`\`javascript
function fibNaive(n) {
  if (n <= 1) return n;
  return fibNaive(n - 1) + fibNaive(n - 2);
}
// Time: O(2^n), Space: O(n)
\`\`\`

#### Memoization Approach
\`\`\`javascript
function fibMemo(n, memo = {}) {
  if (n in memo) return memo[n];
  if (n <= 1) return n;
  
  memo[n] = fibMemo(n - 1, memo) + fibMemo(n - 2, memo);
  return memo[n];
}
// Time: O(n), Space: O(n)
\`\`\`

#### Tabulation Approach
\`\`\`javascript
function fibTab(n) {
  if (n <= 1) return n;
  
  const dp = new Array(n + 1);
  dp[0] = 0;
  dp[1] = 1;
  
  for (let i = 2; i <= n; i++) {
    dp[i] = dp[i - 1] + dp[i - 2];
  }
  
  return dp[n];
}
// Time: O(n), Space: O(n)
\`\`\`

#### Space-Optimized
\`\`\`javascript
function fibOptimized(n) {
  if (n <= 1) return n;
  
  let prev2 = 0;
  let prev1 = 1;
  
  for (let i = 2; i <= n; i++) {
    let current = prev1 + prev2;
    prev2 = prev1;
    prev1 = current;
  }
  
  return prev1;
}
// Time: O(n), Space: O(1)
\`\`\`

### Coin Change Problem

Given coins of different denominations and a target amount, find the minimum number of coins needed.

\`\`\`javascript
class DynamicProgramming {
  // Coin change - minimum coins needed
  static coinChange(coins, amount) {
    const dp = new Array(amount + 1).fill(Infinity);
    dp[0] = 0;
    
    for (let i = 1; i <= amount; i++) {
      for (let coin of coins) {
        if (coin <= i) {
          dp[i] = Math.min(dp[i], dp[i - coin] + 1);
        }
      }
    }
    
    return dp[amount] === Infinity ? -1 : dp[amount];
  }
  
  // Coin change - number of ways
  static coinChangeWays(coins, amount) {
    const dp = new Array(amount + 1).fill(0);
    dp[0] = 1;
    
    for (let coin of coins) {
      for (let i = coin; i <= amount; i++) {
        dp[i] += dp[i - coin];
      }
    }
    
    return dp[amount];
  }
}
\`\`\`

### Longest Common Subsequence (LCS)

\`\`\`javascript
static longestCommonSubsequence(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  return dp[m][n];
}

// Reconstruct the actual LCS
static getLCSString(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
  
  // Fill DP table
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1] + 1;
      } else {
        dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
      }
    }
  }
  
  // Reconstruct LCS
  let lcs = '';
  let i = m, j = n;
  
  while (i > 0 && j > 0) {
    if (str1[i - 1] === str2[j - 1]) {
      lcs = str1[i - 1] + lcs;
      i--;
      j--;
    } else if (dp[i - 1][j] > dp[i][j - 1]) {
      i--;
    } else {
      j--;
    }
  }
  
  return lcs;
}
\`\`\`

### 0/1 Knapsack Problem

\`\`\`javascript
static knapsack01(weights, values, capacity) {
  const n = weights.length;
  const dp = Array(n + 1).fill().map(() => Array(capacity + 1).fill(0));
  
  for (let i = 1; i <= n; i++) {
    for (let w = 1; w <= capacity; w++) {
      if (weights[i - 1] <= w) {
        dp[i][w] = Math.max(
          dp[i - 1][w], // Don't include current item
          dp[i - 1][w - weights[i - 1]] + values[i - 1] // Include current item
        );
      } else {
        dp[i][w] = dp[i - 1][w];
      }
    }
  }
  
  return dp[n][capacity];
}

// Space-optimized version
static knapsackOptimized(weights, values, capacity) {
  const dp = new Array(capacity + 1).fill(0);
  
  for (let i = 0; i < weights.length; i++) {
    for (let w = capacity; w >= weights[i]; w--) {
      dp[w] = Math.max(dp[w], dp[w - weights[i]] + values[i]);
    }
  }
  
  return dp[capacity];
}

// Get items in optimal solution
static knapsackItems(weights, values, capacity) {
  const n = weights.length;
  const dp = Array(n + 1).fill().map(() => Array(capacity + 1).fill(0));
  
  // Fill DP table
  for (let i = 1; i <= n; i++) {
    for (let w = 1; w <= capacity; w++) {
      if (weights[i - 1] <= w) {
        dp[i][w] = Math.max(
          dp[i - 1][w],
          dp[i - 1][w - weights[i - 1]] + values[i - 1]
        );
      } else {
        dp[i][w] = dp[i - 1][w];
      }
    }
  }
  
  // Backtrack to find items
  const items = [];
  let w = capacity;
  
  for (let i = n; i > 0 && w > 0; i--) {
    if (dp[i][w] !== dp[i - 1][w]) {
      items.push(i - 1);
      w -= weights[i - 1];
    }
  }
  
  return items.reverse();
}
\`\`\`

### Edit Distance (Levenshtein Distance)

\`\`\`javascript
static editDistance(str1, str2) {
  const m = str1.length;
  const n = str2.length;
  const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));
  
  // Initialize base cases
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(
          dp[i - 1][j],     // Delete
          dp[i][j - 1],     // Insert
          dp[i - 1][j - 1]  // Replace
        );
      }
    }
  }
  
  return dp[m][n];
}
\`\`\`

### Longest Increasing Subsequence

\`\`\`javascript
static longestIncreasingSubsequence(arr) {
  if (arr.length === 0) return 0;
  
  const dp = new Array(arr.length).fill(1);
  
  for (let i = 1; i < arr.length; i++) {
    for (let j = 0; j < i; j++) {
      if (arr[j] < arr[i]) {
        dp[i] = Math.max(dp[i], dp[j] + 1);
      }
    }
  }
  
  return Math.max(...dp);
}

// O(n log n) solution using binary search
static lisOptimized(arr) {
  if (arr.length === 0) return 0;
  
  const tails = [];
  
  for (let num of arr) {
    let left = 0, right = tails.length;
    
    while (left < right) {
      let mid = Math.floor((left + right) / 2);
      if (tails[mid] < num) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }
    
    if (left === tails.length) {
      tails.push(num);
    } else {
      tails[left] = num;
    }
  }
  
  return tails.length;
}
\`\`\`

## Advanced DP Techniques

### Matrix Chain Multiplication
\`\`\`javascript
static matrixChainOrder(dimensions) {
  const n = dimensions.length - 1;
  const dp = Array(n).fill().map(() => Array(n).fill(0));
  
  // Length of chain
  for (let length = 2; length <= n; length++) {
    for (let i = 0; i <= n - length; i++) {
      let j = i + length - 1;
      dp[i][j] = Infinity;
      
      for (let k = i; k < j; k++) {
        const cost = dp[i][k] + dp[k + 1][j] + 
                    dimensions[i] * dimensions[k + 1] * dimensions[j + 1];
        dp[i][j] = Math.min(dp[i][j], cost);
      }
    }
  }
  
  return dp[0][n - 1];
}
\`\`\`

### Palindrome Partitioning
\`\`\`javascript
static palindromePartitioning(str) {
  const n = str.length;
  const dp = new Array(n).fill(Infinity);
  const isPalindrome = Array(n).fill().map(() => Array(n).fill(false));
  
  // Precompute palindrome information
  for (let i = 0; i < n; i++) {
    for (let j = 0; j <= i; j++) {
      if (str[i] === str[j] && (i - j <= 1 || isPalindrome[j + 1][i - 1])) {
        isPalindrome[j][i] = true;
      }
    }
  }
  
  // DP for minimum cuts
  for (let i = 0; i < n; i++) {
    if (isPalindrome[0][i]) {
      dp[i] = 0;
    } else {
      for (let j = 0; j < i; j++) {
        if (isPalindrome[j + 1][i]) {
          dp[i] = Math.min(dp[i], dp[j] + 1);
        }
      }
    }
  }
  
  return dp[n - 1];
}
\`\`\`

## DP on Trees

### Maximum Path Sum in Binary Tree
\`\`\`javascript
static maxPathSum(root) {
  let maxSum = -Infinity;
  
  function helper(node) {
    if (!node) return 0;
    
    const leftMax = Math.max(0, helper(node.left));
    const rightMax = Math.max(0, helper(node.right));
    
    const currentMax = node.val + leftMax + rightMax;
    maxSum = Math.max(maxSum, currentMax);
    
    return node.val + Math.max(leftMax, rightMax);
  }
  
  helper(root);
  return maxSum;
}
\`\`\`

## DP Optimization Techniques

### Space Optimization
Many 2D DP problems can be optimized to use O(n) space instead of O(n²).

### Memoization Best Practices
\`\`\`javascript
class Memoization {
  constructor() {
    this.cache = new Map();
  }
  
  memoize(fn) {
    return (...args) => {
      const key = JSON.stringify(args);
      
      if (this.cache.has(key)) {
        return this.cache.get(key);
      }
      
      const result = fn.apply(this, args);
      this.cache.set(key, result);
      return result;
    };
  }
  
  // Example usage
  fibonacci = this.memoize((n) => {
    if (n <= 1) return n;
    return this.fibonacci(n - 1) + this.fibonacci(n - 2);
  });
}
\`\`\`

## Applications and Use Cases

### String Algorithms
- Edit distance for spell checkers
- Longest common subsequence for diff tools
- Pattern matching and text analysis

### Optimization Problems
- Resource allocation
- Scheduling problems
- Investment strategies
- Game theory solutions

### Computational Biology
- Sequence alignment
- Gene prediction
- Protein folding

### Machine Learning
- Reinforcement learning (Bellman equations)
- Hidden Markov Models
- Neural network training optimization`
        },
        {
          type: 'interactive_exercise',
          title: 'Dynamic Programming Problem Solver',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive dynamic programming problem solver:

1. Implement classic DP problems (Fibonacci, LCS, Knapsack, Edit Distance)
2. Create both memoization and tabulation versions
3. Build a DP pattern recognition system
4. Implement space optimization techniques
5. Create a solution path reconstruction system

Your solver should handle:
- Multiple DP problem types with automatic pattern detection
- Both top-down and bottom-up approaches
- Space complexity optimization
- Solution reconstruction and explanation
- Performance comparison between approaches

Test with various DP problems and compare memoization vs tabulation performance.`,
            initialCode: `class DynamicProgrammingSolver {
  constructor() {
    this.memoCache = new Map();
    this.problemTypes = {
      fibonacci: this.solveFibonacci.bind(this),
      lcs: this.solveLCS.bind(this),
      knapsack: this.solveKnapsack.bind(this),
      editDistance: this.solveEditDistance.bind(this),
      coinChange: this.solveCoinChange.bind(this)
    };
  }
  
  // Fibonacci with both approaches
  solveFibonacci(n, approach = 'memoization') {
    if (approach === 'memoization') {
      return this.fibMemo(n);
    } else {
      return this.fibTabulation(n);
    }
  }
  
  fibMemo(n, memo = {}) {
    // Implement memoized fibonacci
    // Track number of recursive calls made
  }
  
  fibTabulation(n) {
    // Implement tabulation fibonacci
    // Track space and time usage
  }
  
  // Longest Common Subsequence
  solveLCS(str1, str2, approach = 'tabulation') {
    if (approach === 'memoization') {
      return this.lcsMemo(str1, str2, str1.length, str2.length);
    } else {
      return this.lcsTabulation(str1, str2);
    }
  }
  
  lcsTabulation(str1, str2) {
    // Implement LCS with tabulation
    // Return: { length: number, sequence: string, table: array }
  }
  
  // 0/1 Knapsack Problem
  solveKnapsack(weights, values, capacity, approach = 'tabulation') {
    // Implement both approaches
    // Return: { maxValue: number, items: array, spaceUsed: string }
  }
  
  // Edit Distance (Levenshtein)
  solveEditDistance(str1, str2) {
    // Implement edit distance with operation tracking
    // Return: { distance: number, operations: array }
  }
  
  // Coin Change Problem
  solveCoinChange(coins, amount, type = 'minCoins') {
    // type: 'minCoins' or 'countWays'
    // Return solution with coin breakdown
  }
  
  // Optimize space complexity
  optimizeSpace(problemType, ...args) {
    // Convert 2D DP to 1D where possible
    // Return space-optimized solution
  }
  
  // Compare approaches
  compareApproaches(problemType, ...args) {
    // Run both memoization and tabulation
    // Return: { memoization: object, tabulation: object, recommendation: string }
  }
  
  // Reconstruct solution path
  reconstructSolution(problemType, dpTable, ...args) {
    // Backtrack through DP table to show solution steps
    // Return: array of solution steps with explanations
  }
}

class DPPatternRecognizer {
  // Analyze problem to suggest DP approach
  static analyzePattern(problemDescription) {
    // Check for DP characteristics:
    // - Optimal substructure
    // - Overlapping subproblems
    // Return: { isDPProblem: boolean, suggestedPattern: string, approach: string }
  }
  
  // Generate DP template code
  static generateTemplate(pattern, parameters) {
    // Generate starter code for common DP patterns
    // Return: code template string
  }
}

// Test implementations
const solver = new DynamicProgrammingSolver();

console.log("Testing Fibonacci (n=10):");
const fibResults = solver.compareApproaches('fibonacci', 10);
console.log("Memoization calls:", fibResults.memoization.calls);
console.log("Tabulation space:", fibResults.tabulation.space);
console.log("Recommendation:", fibResults.recommendation);

console.log("\\nTesting LCS:");
const lcsResult = solver.solveLCS("ABCDGH", "AEDFHR");
console.log("LCS length:", lcsResult.length);
console.log("LCS sequence:", lcsResult.sequence);

console.log("\\nTesting Knapsack:");
const weights = [1, 3, 4, 5];
const values = [1, 4, 5, 7];
const knapsackResult = solver.solveKnapsack(weights, values, 7);
console.log("Max value:", knapsackResult.maxValue);
console.log("Selected items:", knapsackResult.items);

console.log("\\nTesting Edit Distance:");
const editResult = solver.solveEditDistance("kitten", "sitting");
console.log("Edit distance:", editResult.distance);
console.log("Operations:", editResult.operations);

console.log("\\nTesting Coin Change:");
const coinResult = solver.solveCoinChange([1, 3, 4], 6, 'minCoins');
console.log("Min coins needed:", coinResult.minCoins);
console.log("Coin breakdown:", coinResult.coins);`,
            expectedOutput: `Testing Fibonacci (n=10):
Memoization calls: 11
Tabulation space: O(n)
Recommendation: tabulation - better space efficiency for this size

Testing LCS:
LCS length: 3
LCS sequence: ADH

Testing Knapsack:
Max value: 9
Selected items: [1, 2]

Testing Edit Distance:
Edit distance: 3
Operations: ['substitute k->s', 'substitute e->i', 'insert g']

Testing Coin Change:
Min coins needed: 2
Coin breakdown: [3, 3]`,
            hints: [
              'Memoization: use Map or object to cache subproblem results',
              'Tabulation: build solution bottom-up using loops and arrays',
              'LCS: if characters match, add 1 to diagonal; else take max of left/above',
              'Knapsack: for each item, choose max of (include item) vs (exclude item)',
              'Edit distance: min of (insert + 1, delete + 1, substitute + cost)',
              'Track operations for solution reconstruction - store which choice was made'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Dynamic Programming Mastery',
          title: 'Advanced Algorithm Design and Optimization',
          points: 150,
          dueWeek: 15
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      duration: '150 minutes',
      points: 250,
      description: 'Comprehensive exam covering data structures (arrays, linked lists, stacks, queues, trees) and basic algorithms with coding implementation.'
    },
    {
      id: 'final',
      title: 'Final Examination',
      type: 'exam',
      date: 'Week 15',
      duration: '180 minutes',
      points: 300,
      description: 'Cumulative exam covering all course material including advanced algorithms, dynamic programming, and complexity analysis.'
    },
    {
      id: 'algorithm-portfolio',
      title: 'Algorithm Implementation Portfolio',
      type: 'project',
      date: 'Week 15',
      duration: '3 weeks',
      points: 200,
      description: 'Comprehensive portfolio demonstrating mastery of data structures and algorithms through optimized implementations and analysis.'
    }
  ],
  resources: [
    {
      category: 'Textbooks',
      items: [
        'Introduction to Algorithms by Cormen, Leiserson, Rivest, and Stein (CLRS)',
        'Algorithm Design by Kleinberg and Tardos',
        'Data Structures and Algorithms in JavaScript by McMillan',
        'Cracking the Coding Interview by Gayle McDowell'
      ]
    },
    {
      category: 'Online Platforms',
      items: [
        'LeetCode for algorithm practice',
        'HackerRank coding challenges',
        'Codeforces competitive programming',
        'GeeksforGeeks algorithm tutorials',
        'Visualgo.net for algorithm visualization'
      ]
    },
    {
      category: 'Development Tools',
      items: [
        'JavaScript runtime (Node.js)',
        'Code editor with debugging support',
        'Algorithm visualization tools',
        'Performance profiling tools',
        'Version control (Git) for project management'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Module Assignments': '25%',
    'Coding Projects': '20%',
    'Midterm Exam': '25%',
    'Final Exam': '30%'
  },
  attendancePolicy: 'Regular attendance essential for hands-on coding sessions and algorithm implementation workshops. Active participation required in code reviews and problem-solving discussions.',
  latePolicy: 'Algorithm assignments penalized 15% per day late due to the cumulative nature of data structure knowledge. Extensions available for complex optimization projects.',
  accommodations: 'Accommodations available including extended time for coding assignments, alternative assessment formats, and assistive technology for algorithm visualization.',
  academicIntegrity: 'Original algorithm implementations required with proper complexity analysis. Code discussion encouraged, but individual problem solutions must be independently developed.',
  technologyRequirements: 'Computer capable of running development environments, algorithm visualization tools, and coding platforms. Reliable internet access for online coding practice.',
  softwareRequirements: [
    'JavaScript development environment (Node.js)',
    'Code editor with debugging capabilities',
    'Algorithm visualization software',
    'Online coding platform access',
    'Performance analysis tools',
    'Git version control system'
  ],
  courseEvaluation: 'Student feedback collected through algorithm implementation reviews, coding interview simulations, and comprehensive technical skill assessments.'
};